import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import sessionReducer from "@redux/slices/session-slice";
import counterReducer from "@redux/slices/quiz-step.slice";
import patientProfileReducer from "@redux/slices/patient/patient-profile.slice";
import practitionerProfileReducer from "@redux/slices/practitioner/practitioner-profile";
import patientBackgroundReducer from "@redux/slices/patient/patient-background.slice";
import patientDiagnosesReducer from "@redux/slices/patient/patient-diagnoses.slice";
import patientIdentifierReducer from "@redux/slices/patient/patient-identifier.slice";
import patientRecordsReducer from "@redux/slices/patient/patient-records.slice";
import patientReducer from "@redux/slices/patient/patient.slice";
import recordReducer from "@redux/slices/records/records.slice";
import documentsReducer from "@redux/slices/documents/documents.slice";
import patientTreatmentsReducer from "@redux/slices/patient/patient-treatments.slice";
import newAppointmentReducer from "@redux/slices/appoinments/appointments.slice";
import organizationsReducer from "@redux/slices/practitioner/practitioner-organization";
import walkthroughSlice from "@redux/slices/walkthrough/walkthrough.slice";
import accountSlice from "@redux/slices/financial/account";
import createAppointment from "./slices/appoinments/create-appointment";
import consultingRooms from "./slices/appoinments/consulting-rooms";
import organizationSlice from "./slices/organization/related-organizations-slice";
import CalendarHeaderRe from "./slices/calendar/header/calendar-header.slice";
import practitionerAppointments from "./slices/appoinments/practitionerAppointments.slice";
import practitionerAppointmentsToBeConfirmed from "./slices/appoinments/practitionerAppointmentsToBeConfirmed.slice";
import appointmentCheckInSlice from "./slices/appoinments/appointmentCheckIn.slice";
import chatArriveFlag from "@redux/slices/socketChat/chatMessageArrive-slice";

import thunk from "redux-thunk";

import { environment } from "../environments/environment";
import voiceCallSlice from "./slices/voice-call/voice-call.slice";
import voiceAlertSlice from "./slices/voice-call/voice-alert.slice";
import sessionSocketChatSlice from "./slices/socketChat/loginChat.slice";
import messageSocketQueueSlice  from "./slices/socketChat/chatMessages";
import contactSocketQueueSlice from "./slices/socketChat/chatContacts";
import chatSocketQueueSlice from "./slices/socketChat/chatChats";
import loggedValidationSlice from "./slices/socketChat/loggedValidation";
import chatSocketStateSlice from "./slices/socketChat/storeForRender/chatSocketState";
import chatConfigSlice from "@redux/slices/socketChat/chatConfig";
import chatContactStatusSlice from "./slices/socketChat/chatStatus";
import videoCallSlice from "./slices/video-call/video-call.slice";
import videoCallAlertSlice from "./slices/video-call/videoCall-alert.slice";
import messageSocketUnreadMessageSlice from "./slices/socketChat/chatUnreadMessages";
import practitionerPermissionSlice from "./slices/practitioner/practitioner-permissions";
import practitionerModulesSlice from "./slices/practitioner/practitioner-modules";
import ethnicityReducer from "./slices/patient/ethnicity.slice";
import documentDevicesOrderSlice from "./slices/appoinments/document-device-order.slice";
import patientPreviousReasonsSlice  from "./slices/patient/patient-prevReasons.slice";
import selectedRecordSlice from "./slices/records/selected-record.slice";
import redeemCurrentStateSlice from "./slices/redeemCurrentState";
import newPregnancySlice from "./slices/woman/pregnancy";
import newChildRecordSlice from "./slices/child/child-record";
import patientDetailSlice from "./slices/patient/patient-detail.slice";
import subscriptionSlice from "./slices/subscription/subscription-slice";

const reducers = combineReducers({
    session: sessionReducer,
    stepCounter: counterReducer,
    patientProfile: patientProfileReducer,
    patientBackground: patientBackgroundReducer,
    patientDiagnoses: patientDiagnosesReducer,
    patientIdentifier: patientIdentifierReducer,
    patientRecords: patientRecordsReducer,
    patientTreatments: patientTreatmentsReducer,
    organizations: organizationsReducer,
    record: recordReducer,
    /**
     * @deprecated Replace by `PatientDetailsContext` context where possible.
     */
    patient: patientReducer,
    practitionerProfile: practitionerProfileReducer,
    newAppointment: newAppointmentReducer,
    docs: documentsReducer,
    walkthrough: walkthroughSlice,
    accountDoctorProx: accountSlice,
    createAppointment: createAppointment,
    rooms: consultingRooms,
    relatedOrganization: organizationSlice,
    calendarHeaderRe:CalendarHeaderRe,
    practitionerAppointments:practitionerAppointments,
    practitionerAppointmentsToBeConfirmed:practitionerAppointmentsToBeConfirmed,
    appointmentCheckIn:appointmentCheckInSlice,
    voiceCall: voiceCallSlice,
    chatArrive:chatArriveFlag,
    voiceAlert: voiceAlertSlice,
    sessionSocketChat: sessionSocketChatSlice,
    messageSocketQueue: messageSocketQueueSlice,
    contactSocketQueue: contactSocketQueueSlice,
    chatSocketQueue: chatSocketQueueSlice,
    loggedInValidation: loggedValidationSlice,
    chatSocketState: chatSocketStateSlice,
    chatConfig: chatConfigSlice,
    chatContactStatus: chatContactStatusSlice,
    videoCall: videoCallSlice,
    videoAlert: videoCallAlertSlice,
    messageSocketUnreadMessage : messageSocketUnreadMessageSlice,
    permission : practitionerPermissionSlice,
    practitionerModules:practitionerModulesSlice,
    ethnicityList: ethnicityReducer,
    documentDevicesOrder: documentDevicesOrderSlice,
    patientPreviousReasons: patientPreviousReasonsSlice,
    selectedRecord: selectedRecordSlice,
    redeemCurrentState: redeemCurrentStateSlice,
    newPregnancy: newPregnancySlice,
    childRecord: newChildRecordSlice,
    patientDetailRdx: patientDetailSlice,
    subscriptionSlice: subscriptionSlice,
});

const persistConfig = {
    key: "root",
    storage,
};

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
    reducer: persistedReducer,
    devTools: environment.qa === false && environment.production === false,
    middleware: [thunk],
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
